import { RecalculateAppetitesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * Outcome of a recalculate-appetites call. `errorCode` is a *successful* gating
 * outcome (the cascade was skipped because the org isn't configured for it),
 * surfaced so the caller can show a specific message. `null` means the cascade
 * ran. The string values match across the Apollo and tRPC paths — both the
 * GraphQL `Recalculate_Appetites_Error_Code_Enum` and the tRPC response use
 * `'cascade_not_configured'` / `'cascade_not_enabled'`.
 */
export interface RecalculateAppetitesResult {
  errorCode: string | null;
}

/**
 * Flag-gated recalculateAppetites (RSP-6486) — a no-input trigger. On the `trpc`
 * flag it routes through the tRPC async-request path (server-side cascade
 * compute); otherwise the legacy Apollo mutation. Both paths converge on the
 * same `{ errorCode }` shape (the tRPC result is already typed; the Apollo
 * `recalculateAppetites.errorCode` is coalesced in `mapGraphqlResponse`) so the
 * consumer's branching is path-agnostic.
 */
export const useRecalculateAppetites = createMutationHook({
  name: 'recalculateAppetites',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.aggregation.recalculateAppetites.mutationOptions({
      onSuccess,
    }),
  mapInput: (_input: void) => undefined,
  // COI-889: safe to invalidate — `executeAsyncRequest` polls until the
  // AppetitesRecalculated completion event lands, so the cascade has run.
  invalidate: (trpc) => [
    trpc.frontend.appetite.pathKey(),
    trpc.frontend.risk.pathKey(),
  ],
  mapResponse: (result: {
    errorCode: string | null;
  }): RecalculateAppetitesResult => ({ errorCode: result.errorCode }),
  graphqlDocument: RecalculateAppetitesDocument,
  graphqlVariables: () => ({}),
  mapGraphqlResponse: (data): RecalculateAppetitesResult => ({
    errorCode: data.recalculateAppetites?.errorCode ?? null,
  }),
});
